import { useEffect, useRef, useState } from "react";
import { PlayCircle } from "lucide-react";

/* Bicarra product videos (BC-014).

   The files live outside the React build, in /media/v1/ (nginx alias to
   /opt/apps/airizbot-media/v1), so a frontend rebuild never copies 140 MB
   of video and old builds stay small. Bump the folder (v2, v3) when a video
   is replaced, so browsers never keep a stale copy.

   Language follows the site preference stored under "bicarra.lang" (the
   same key Sign in, Register and Settings use). With nothing stored, a
   browser set to Malay gets the Bahasa Melayu cut; everyone else gets
   English. The EN | BM switch on each player writes the same key, and every
   player on the page follows it. */

const LANG_KEY = "bicarra.lang";
const LANG_EVENT = "bicarra:lang";
const BASE = "/media/v1";

export function getSiteLang() {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v === "ms" || v === "en") return v;
  } catch { /* storage blocked: fall through */ }
  try {
    const nav = (navigator.languages && navigator.languages[0]) || navigator.language || "";
    if (/^(ms|id)\b/i.test(nav)) return "ms";
  } catch { /* no navigator */ }
  return "en";
}

export function useSiteLang() {
  const [lang, setLangState] = useState(getSiteLang);
  useEffect(() => {
    const sync = () => setLangState(getSiteLang());
    window.addEventListener(LANG_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(LANG_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  const setLang = (next) => {
    try { localStorage.setItem(LANG_KEY, next); } catch { /* ignore */ }
    setLangState(next);
    try { window.dispatchEvent(new Event(LANG_EVENT)); } catch { /* ignore */ }
  };
  return [lang, setLang];
}

/* Chapter times come from the tutorial chapter files delivered with the
   videos (Video Bicarra 2026-10-04). */
const CHAPTERS = {
  ms: [
    [0, "Pengenalan"], [12, "Cipta akaun anda"], [27, "Cipta pembantu"],
    [42, "Tambah data latihan"], [69, "Pilih model AI"], [96, "Jadikan milik anda"],
    [120, "Uji pembantu"], [135, "Kongsi pembantu"], [158, "Susulan"], [177, "Penutup"],
  ],
  en: [
    [0, "Introduction"], [10, "Create your account"], [22, "Create an assistant"],
    [37, "Add training data"], [65, "Choose your AI model"], [90, "Make it yours"],
    [111, "Test it"], [124, "Share your assistant"], [145, "Follow up"], [164, "Wrap-up"],
  ],
};

const LABELS = {
  intro: {
    en: { title: "Bicarra in two minutes", caption: "Captions", length: "2 min" },
    ms: { title: "Bicarra dalam dua minit", caption: "Sari kata", length: "2 min" },
  },
  tutorial: {
    en: { title: "Tutorial: build your first assistant", caption: "Captions", length: "3 min", chapters: "Chapters" },
    ms: { title: "Tutorial: bina pembantu pertama anda", caption: "Sari kata", length: "3 min", chapters: "Bab" },
  },
};

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export function LangSwitch({ lang, onChange, className = "" }) {
  const opt = (code, text, label) => (
    <button
      type="button"
      onClick={() => onChange(code)}
      aria-pressed={lang === code}
      aria-label={label}
      className={`h-7 px-3 rounded-full text-[12px] font-semibold transition-colors ${
        lang === code ? "bg-b2-indigo text-white" : "text-b2-inksoft hover:text-b2-ink"
      }`}
    >
      {text}
    </button>
  );
  return (
    <div className={`inline-flex items-center gap-0.5 p-0.5 rounded-full bg-white border border-b2-line ${className}`}
         role="group" aria-label="Video language">
      {opt("en", "EN", "English video")}
      {opt("ms", "BM", "Video Bahasa Melayu")}
    </div>
  );
}

/* kind: "intro" | "tutorial". showChapters only applies to the tutorial. */
export default function BicarraVideo({ kind = "intro", showChapters = false, showSwitch = true, className = "" }) {
  const [lang, setLang] = useSiteLang();
  const ref = useRef(null);
  const name = `bicarra-${kind}-${lang}`;
  const L = LABELS[kind][lang];
  const chapters = kind === "tutorial" ? CHAPTERS[lang] : null;

  const seek = (t) => {
    const v = ref.current;
    if (!v) return;
    try {
      v.currentTime = t;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    } catch { /* metadata not loaded yet; the play button still works */ }
  };

  return (
    <div className={className} data-testid={`video-${kind}`}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <p className="inline-flex items-center gap-2 text-[14px] font-semibold text-b2-ink">
          <PlayCircle className="w-[18px] h-[18px] text-b2-indigo" strokeWidth={1.9} />
          {L.title}
          <span className="font-normal text-b2-faint">· {L.length}</span>
        </p>
        {showSwitch && <LangSwitch lang={lang} onChange={setLang} />}
      </div>
      <div className="rounded-[16px] overflow-hidden bg-black border border-black/[0.06] aspect-video">
        {/* key forces a fresh element when the language changes, so the
            browser drops the old source instead of keeping its buffer. */}
        <video
          key={name}
          ref={ref}
          className="w-full h-full"
          controls
          playsInline
          preload="none"
          poster={`${BASE}/${name}.jpg`}
          lang={lang}
        >
          <source src={`${BASE}/${name}.mp4`} type="video/mp4" />
          <track kind="subtitles" src={`${BASE}/${name}.vtt`} srcLang={lang} label={L.caption} />
        </video>
      </div>
      {showChapters && chapters && (
        <div className="mt-4">
          <p className="text-[12px] font-semibold tracking-[0.12em] uppercase text-b2-faint">{L.chapters}</p>
          <ol className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
            {chapters.map(([t, label]) => (
              <li key={t}>
                <button
                  type="button"
                  onClick={() => seek(t)}
                  className="w-full text-left flex items-baseline gap-3 py-1 text-[14px] text-b2-inksoft hover:text-b2-indigo"
                >
                  <span className="tabular-nums text-b2-indigo font-medium w-10 shrink-0">{fmt(t)}</span>
                  {label}
                </button>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
